/**
 * LAB-FE-1 — how a Test Request READS, once, for every screen that shows one:
 * the Test Register, the request page, the ITC line and the laboratory portal
 * (approved prototype `docs/design/mockups/prototipo-lab-v1.html`, `ST`,
 * `chip()`, `fD()`/`fmt()`).
 *
 * `state` is the server's composite (`lab_tr_state`): issued | awaiting_results
 * | awaiting_review | returned | accepted_pass | accepted_fail | cancelled. The
 * screens never derive it from `status` + `result` themselves (D20.5).
 */

import { fmtShortDay } from '../../lib/utils';

export const STATE_LABEL = {
  issued: 'Issued to laboratory',
  awaiting_results: 'Awaiting results',
  awaiting_review: 'Awaiting engineering review',
  returned: 'Returned to laboratory',
  accepted_pass: 'Accepted · Pass',
  accepted_fail: 'Accepted · Fail',
  cancelled: 'Cancelled',
};

/** The chip tone of a state (HoBits `Chip` tones + `bad`). */
export const stateTone = (state) => {
  if (state === 'accepted_pass') return 'done';
  if (state === 'accepted_fail') return 'bad';
  if (state === 'cancelled') return 'void';
  if (state === 'returned') return 'flag';
  return 'state';
};

export const LIVE_STATES = ['issued', 'awaiting_results', 'awaiting_review', 'returned'];

/** A calendar date the server serves as `YYYY-MM-DD` (the PROJECT's calendar):
 *  "Wed 1 Oct 2026" (DATES-1: the app's one day, `fmtShortDay`, with the
 *  year). Read as a date, never as an instant, so no timezone moves it. */
export const fDay = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
  if (!m) return '—';
  return fmtShortDay(`${m[1]}-${m[2]}-${m[3]}`) || '—';
};

/** An instant: "Mon 21 Sep 2026, 09:00" (DATES-1: the app's one instant,
 *  `fmtShortDay`, 24 h), in `tz` when given (the project's), else `TIMEZONE`. */
export const fAt = (iso, tz) => {
  if (!iso) return '—';
  if (Number.isNaN(new Date(iso).getTime())) return '—';
  return fmtShortDay(iso, tz || '', { time: true }) || '—';
};

/** "F12 +1 more" — the first covered asset and how many others. */
export const coversShort = (covers) => {
  const list = Array.isArray(covers) ? covers : [];
  if (!list.length) return '—';
  const first = list.find((c) => c.is_origin) || list[0];
  const label = first.asset_number || first.asset_name || '—';
  return list.length > 1 ? `${label} +${list.length - 1} more` : label;
};

/** LAB-FE-2 B1 — "ITC-0014 · line 8": the ITC's number (ITC-NUM-1) and the
 *  line. `itc_number` is `null` for an ITC created before the number and not
 *  yet migrated — then "line 8" alone. Empty when neither is served. */
export const itcLine = (itcNumber, itemNo) => [
  itcNumber || null,
  itemNo ? `line ${itemNo}` : null,
].filter(Boolean).join(' · ');

/** "ITC-0014 · line 8 · ITC-0015 · line 8" — the ITC lines a request covers. */
export const coversLines = (covers) => (Array.isArray(covers) ? covers : [])
  .map((c) => (c.item_no ? itcLine(c.itc_number, c.item_no) : null)).filter(Boolean).join(' · ');

/** Default required date of a new request: ten days from today (the prototype's). */
export const defaultRequiredDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 10);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export const TWO_FAILS = 'Two failed results on this line. Raise an NCR before requesting another test.';
